import { GuestProfile, GuestProfileForm } from "@closerplatform/spinner-openapi";
import { AsyncAutoComplete } from "@fidget/common/components/form/asyncAutoComplete/asyncAutoComplete";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { ResponseError } from "@fidget/common/models/responseError";
import { HttpStatusCode } from "@fidget/common/utils/httpStatusCodes";
import { TimeUtils } from "@fidget/common/utils/timeUtils";
import classNames from "classnames";
import { upperFirst } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { FormattedDate, FormattedTime } from "react-intl";
import { DropdownMeetingEditForm } from "../../containers/calendar/dropdownMeetingEdit";
import { useStore } from "../../models";
import { useAgentServices } from "../../models/agentServices";
import { IGuest } from "../../models/base/Guest";
import { ICalendarMeeting } from "../../models/calendar/CalendarMeeting";
import {
  isDelete,
  isEdit,
  isReschedule,
  isSchedule,
  isScheduleExisting,
  isScheduleNew,
  isSearch,
  isView,
  ScheduleNew
} from "../../models/calendar/MeetingPopup";
import { IRoom } from "../../models/room/Room";
import { GuestDropdownItemGuestProfile } from "../guestDropdown/guestDropdownItem";
import { ProfileData, ProfileEditForm } from "../profile/profileEditForm";
import styles from "./meetingPopup.css";

interface MeetingDetailsProps {
  start: Date;
  duration: number;
  room?: IRoom;
}

const MeetingDetails = ({ start, duration, room }: MeetingDetailsProps) => {
  const { calendarStore } = useStore();

  return (
    <div>
      <div className={styles["meeting-popup-client"]}>{room && room.owner.displayName}</div>
      <div className={styles["meeting-relative-date"]}>
        {upperFirst(intl.formatRelativeDays(start))}
      </div>
      <div
        className={classNames([styles["meeting-popup-date"], styles.editable])}
        onClick={() => calendarStore.createEditPopup("date")}
      >
        <div>
          <FormattedDate value={start} weekday={"short"} day={"2-digit"} month={"2-digit"} />
        </div>
        <div className={styles["edit-icon"]}>
          <i className={"icon-pencil"} />
        </div>
      </div>
      <div
        className={classNames([styles["meeting-popup-time"], styles.editable])}
        onClick={(e) => {
          e.stopPropagation();
          calendarStore.createEditPopup("start");
        }}
      >
        <div>
          <span className={styles.time} onClick={() => calendarStore.createEditPopup("start")}>
            <FormattedTime value={start} />
          </span>
          <span className={styles["time-divider"]}>―</span>
          <span className={styles.time} onClick={() => calendarStore.createEditPopup("end")}>
            <FormattedTime value={start.getTime() + duration} />
          </span>
        </div>
        <div className={styles["edit-icon"]}>
          <i className={"icon-pencil"} />
        </div>
      </div>
    </div>
  );
};

const GuestDetails = (props: { profile: IGuest }) => {
  return (
    (props.profile && (
      <div className={styles["guest-details"]}>
        <div className={styles["guest-name"]}>
          <div>{props.profile.lastName}</div>
          <div>{props.profile.firstName}</div>
        </div>
        <div className={styles["guest-email-and-phone"]}>
          <div>{props.profile.email}</div>
          {props.profile.phone && (
            <div>
              {props.profile.phone.region} {props.profile.phone.number}
            </div>
          )}
        </div>
      </div>
    )) || <div />
  );
};

const ScheduleError = (props: { error: string }) => (
  <div className={styles["meeting-popup-error"]}>
    <i className={"icon-exclamation-mark"} />
    <span>{props.error}</span>
  </div>
);

const getError = (meetings: Array<ICalendarMeeting>, start: Date, duration: number) => {
  if (isNaN(start.getTime())) {
    return intl.formatMessage("calendar.meeting.error.incorrectDateFormat");
  }
  if (start.getTime() <= Date.now()) {
    return intl.formatMessage("calendar.meeting.error.pastTimeSelected");
  }
  if (TimeUtils.overlapsWithMeeting(meetings, start, duration)) {
    return intl.formatMessage("calendar.meeting.error.slotAlreadyBooked");
  } else {
    return undefined;
  }
};

interface UserFormProps {
  schedule: ScheduleNew;
}

const ScheduleExternalForm = observer(({ schedule }: UserFormProps) => {
  const { calendarStore } = useStore();

  // TODO: refact. regr.: should send errors to logger; was: `logger.error(this.state.errors);`
  const handleFormSubmit = async (data: ProfileData) => {
    // at this point ProfileEditForm is validated
    const start = schedule.start.getTime();

    const form: GuestProfileForm = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: {
        number: data?.phoneNumber || ""
      }
    };

    await calendarStore.createExternalMeetingRequest(start, schedule.duration, form);
  };

  return (
    <div className={styles["meeting-popup"]}>
      <div className={styles.header}>
        <span className={styles["back-icon"]} onClick={() => calendarStore.createSearchPopup()}>
          <i className="icon-arrow" />
        </span>
        <L10n id={"calendar.meeting.title"} />
      </div>
      <ProfileEditForm requireContact className={styles["client-form"]} onSubmit={handleFormSubmit}>
        <DropdownMeetingEditForm className={styles.time} popupState={schedule} />
        <AgentButton type="submit" fullWidth>
          {renderConfirmButton("create")}
        </AgentButton>
        <AgentButton
          onClick={() => calendarStore.dismissPopup()}
          buttonStyle={"outlined secondary"}
          fullWidth
        >
          <L10n id={"calendar.meeting.cancel"} />
        </AgentButton>
      </ProfileEditForm>
    </div>
  );
});

export const MeetingPopup = observer(() => {
  const { calendarStore, roomStore } = useStore();
  const { spinnerService } = useAgentServices();

  const handleEscape = (event: any) => {
    if (event.keyCode === 27) {
      calendarStore.dismissPopup();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleEscape);

    return document.removeEventListener("keydown", handleEscape);
  }, []);

  const handleScheduleError = (error: ResponseError) => {
    if (error.response.status === HttpStatusCode.CONFLICT) {
      alert(intl.formatMessage("calendar.meeting.error.alertSlotAlreadyTaken"));
    }
  };

  const render = () => {
    const state = calendarStore.popup;
    if (!state) {
      return <></>;
    }

    const room = (state.roomId && roomStore.rooms.get(state.roomId)) || undefined;

    if (isSchedule(state)) {
      return (
        <div className={styles["meeting-popup"]}>
          <MeetingDetails room={room} start={state.start} duration={state.duration} />
          <AgentButton
            onClick={() =>
              calendarStore
                .createMeetingRequest(state.roomId, state.start, state.duration)
                .catch((e: ResponseError) => handleScheduleError(e))
            }
            fullWidth
          >
            {renderConfirmButton("create")}
          </AgentButton>
          <AgentButton
            onClick={() => calendarStore.dismissPopup()}
            fullWidth
            buttonStyle={"outlined secondary"}
          >
            <L10n id={"popup.button.cancel"} />
          </AgentButton>
        </div>
      );
    } else if (isReschedule(state)) {
      return (
        <div className={styles["meeting-popup"]}>
          <MeetingDetails room={room} start={state.start} duration={state.duration} />
          <AgentButton
            onClick={() =>
              calendarStore
                .createMeetingRequest(state.roomId, state.start, state.duration, state.selected)
                .catch((e: ResponseError) => handleScheduleError(e))
            }
            fullWidth
          >
            {renderConfirmButton("edit")}
          </AgentButton>
          <AgentButton
            onClick={() => calendarStore.dismissPopup()}
            fullWidth
            buttonStyle={"outlined secondary"}
          >
            <L10n id={"calendar.meeting.cancel"} />
          </AgentButton>
        </div>
      );
    } else if (isView(state)) {
      return (
        <div className={classNames([styles["meeting-popup"], styles.inverse])}>
          <MeetingDetails room={room} start={state.start} duration={state.duration} />
          <div className={styles["meeting-popup-actions"]}>
            <AgentButton
              onClick={() => calendarStore.switchPopupToDeleteMode()}
              id={"meeting_popup_delete"}
            >
              <L10n id={"popup.button.delete"} />
            </AgentButton>
            <AgentButton
              onClick={() => calendarStore.dismissPopup()}
              id={"meeting_popup_close"}
              buttonStyle={"outlined primary"}
            >
              <L10n id={"popup.button.close"} />
            </AgentButton>
          </div>
        </div>
      );
    } else if (isDelete(state)) {
      return (
        <div className={classNames([styles["meeting-popup"], styles.inverse])}>
          <div className={styles["meeting-popup-query"]}>
            <L10n id={"calendar.meeting.delete.confirmation"} />
          </div>
          <div className={styles["meeting-popup-actions"]}>
            <AgentButton
              onClick={() => calendarStore.switchPopupToViewMode()}
              id={"meeting_popup_delete_no"}
            >
              <L10n id={"calendar.meeting.delete.no"} />
            </AgentButton>
            <AgentButton
              onClick={() => calendarStore.cancelMeetingRequest(state.selected)}
              id={"meeting_popup_delete_yes"}
              buttonStyle={"outlined primary"}
            >
              <L10n id={"calendar.meeting.delete.yes"} />
            </AgentButton>
          </div>
        </div>
      );
    } else if (isEdit(state)) {
      const { meetings } = calendarStore;
      const otherMeetings = state.selected
        ? meetings.filter((m: ICalendarMeeting) => m.id !== state.selected)
        : meetings;
      const error = getError(otherMeetings, state.start, state.duration);
      const guestDisplayName = (state.profile && state.profile.displayName) || "";
      return (
        <div className={styles["meeting-popup"]}>
          <form>
            <div className={styles["meeting-popup-client"]}>{guestDisplayName}</div>
            <DropdownMeetingEditForm popupState={state} />
            {error && <ScheduleError error={error} />}
            <AgentButton
              onClick={() =>
                !error &&
                calendarStore
                  .createMeetingRequest(state.roomId, state.start, state.duration, state.selected)
                  .catch((e: ResponseError) => handleScheduleError(e))
              }
              fullWidth
              id={"meeting_edit_save_button"}
            >
              {renderConfirmButton("edit")}
            </AgentButton>
            <AgentButton
              onClick={() => calendarStore.dismissPopup()}
              buttonStyle={"outlined secondary"}
              fullWidth
              id={"meeting_edit_cancel_button"}
            >
              <L10n id={"calendar.meeting.cancel"} />
            </AgentButton>
          </form>
        </div>
      );
    } else if (isScheduleExisting(state) && state.profile) {
      const error = getError(calendarStore.meetings, state.start, state.duration);
      return (
        <div className={styles["meeting-popup"]}>
          <form>
            <div className={styles.header}>
              <div
                className={styles["back-icon"]}
                onClick={() => calendarStore.createSearchPopup()}
              >
                <i className="icon-arrow" />
              </div>
              <L10n id={"calendar.meeting.title"} />
            </div>
            <GuestDetails profile={state.profile} />
            <DropdownMeetingEditForm popupState={state} />
            {error && <ScheduleError error={error} />}
            <AgentButton
              onClick={() =>
                !error &&
                calendarStore
                  .createMeetingRequest(state.profile.roomId!, state.start, state.duration)
                  .catch((e: ResponseError) => handleScheduleError(e))
              }
              fullWidth
            >
              {renderConfirmButton("create")}
            </AgentButton>
            <AgentButton
              onClick={() => calendarStore.dismissPopup()}
              buttonStyle={"outlined secondary"}
              fullWidth
            >
              <L10n id={"calendar.meeting.cancel"} />
            </AgentButton>
          </form>
        </div>
      );
    } else if (isScheduleNew(state)) {
      return <ScheduleExternalForm schedule={state} />;
    } else if (isSearch(state) || isScheduleExisting(state)) {
      return (
        <div className={classNames([styles["meeting-popup"], styles["search-form"]])}>
          <div className={styles.header}>
            <L10n id={"calendar.meeting.title"} />
          </div>
          <AsyncAutoComplete<GuestProfile>
            itemsProvider={(query) => spinnerService.searchGuest(query)}
            onChange={(profile) => {
              calendarStore.createPopupWithGuest(profile);
            }}
            className={styles["search-input"]}
            placeholder={intl.formatMessage("calendar.meeting.searchClientPlaceholder")}
            GridItem={GuestDropdownItemGuestProfile}
          />
          <div className={styles.or}>
            <L10n id={"calendar.meeting.or"} />
          </div>
          <AgentButton onClick={() => calendarStore.createPopupWithNewGuest()} fullWidth>
            <L10n id={"calendar.meeting.createNewCustomer"} />
          </AgentButton>
          <AgentButton
            onClick={() => calendarStore.dismissPopup()}
            fullWidth
            buttonStyle={"outlined secondary"}
          >
            <L10n id={"popup.button.cancel"} />
          </AgentButton>
        </div>
      );
    } else {
      return <div />;
    }
  };

  return render();
});

const renderConfirmButton = (mode: "create" | "edit"): React.ReactNode => {
  switch (mode) {
    case "create":
      return <L10n id={"calendar.meeting.confirm.create"} />;
    case "edit":
      return <L10n id={"calendar.meeting.confirm.edit"} />;
    default:
      return undefined;
  }
};
